import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, connect, useSelector } from 'react-redux';
import {
  ClickableTile,
  Button,
  OverflowMenu,
  OverflowMenuItem,
  InlineLoading
} from 'carbon-components-react';
import { ChevronRight16, Close20 } from '@carbon/icons-react';
import { CFCProps, SCREEN_SIZE } from 'types';
import { fetchTopThreeCLO } from 'redux/clo';
import { RootState } from 'redux/root.reducer';
import { setShowCLOListing } from 'redux/app/actions';
import {
  selectTopThreeCLO,
  selectLoadingTopThreeCLO,
  selectRefreshTopThreeCLO
} from 'redux/clo/selectors';
import { selectShowCLOListing } from 'redux/app/selectors';
import { selectUser } from 'redux/user/selector';
import { selectIsAuthenticated } from 'redux/session/selectors';
import { ReactComponent as FivePLogo } from 'assets/icons/5-p.svg';
import { ReactComponent as CLOLogo } from 'assets/icons/clo.svg';
import { ReactComponent as FivePLogo40 } from 'assets/icons/5-p-40.svg';
import { ReactComponent as CLOLogo40 } from 'assets/icons/clo-40.svg';

const mapStateToProps = ({ cloCache }: RootState) => ({
  topThreeCLO: cloCache?.topThreeCLO,
  isLoadingTopThreeCLO: cloCache?.loadingTopThreeCLO
});
const connector = connect(mapStateToProps);

type OwnProps = {
  screenSize: String;
  handleCLOTileClick: Function;
  handleSeeALLCLOButtonClick: Function;
  handle5PsGuideClick: Function;
  enquiryDetail: any;
  setQuerySeed: Function;
  querySeed: string;
};

type Props = CFCProps<typeof connector> & OwnProps;

const CLOSection: React.FC<Props | any> = (props: Props) => {
  const {
    screenSize,
    handleCLOTileClick,
    handleSeeALLCLOButtonClick,
    handle5PsGuideClick,
    enquiryDetail,
    setQuerySeed,
    querySeed
  }: any = props;
  const dispatch = useDispatch();
  const topThreeCLO = useSelector(selectTopThreeCLO);
  const loadingTopThreeCLO = useSelector(selectLoadingTopThreeCLO);
  const refreshTopThreeCLO = useSelector(selectRefreshTopThreeCLO);
  const showCLOListing = useSelector(selectShowCLOListing);
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [showCLO, setShowCLO] = useState<boolean>(false);

  const getTop3CLO = useCallback(
    (params: any) => {
      dispatch(fetchTopThreeCLO(params));
    },
    [dispatch]
  );

  useEffect(() => {
    if (
      enquiryDetail &&
      Object.keys(enquiryDetail).length !== 0 &&
      isAuthenticated
    ) {
      const params = {
        filter: 'top3:true,withHidden:false',
        limit: 10,
        offset: 0,
        include: '',
        extent: enquiryDetail.shape,
        fields: 'id,name,details',
        orderBy: '',
        seed: querySeed
      };

      if (refreshTopThreeCLO) {
        getTop3CLO(params);
      }
    }
  }, [
    enquiryDetail,
    getTop3CLO,
    querySeed,
    refreshTopThreeCLO,
    isAuthenticated
  ]);

  useEffect(() => {
    if (showCLOListing && user) {
      let showFlag = showCLOListing?.[user?.id];
      if (showCLOListing?.[user?.id] === undefined) {
        showFlag = true;
      }
      setShowCLO(showFlag);
    } else if (showCLOListing === undefined) {
      setShowCLO(true);
    }
  }, [user, showCLOListing]);

  const toggleCLOListing = (toggle: boolean, sticky: boolean = false) => {
    setShowCLO(toggle);
    const data = showCLOListing || {};
    data[user.id] = toggle;
    if (sticky) {
      dispatch(setShowCLOListing(data));
    }
  };

  const handleTileClick = (cloId: number) => {
    const cloListInfo = topThreeCLO.Info;
    !querySeed && setQuerySeed(cloListInfo.seed);
    handleCLOTileClick(cloId);
  };

  const handleSeeAllCLOClick = () => {
    const cloListInfo = topThreeCLO.Info;
    !querySeed && setQuerySeed(cloListInfo.seed);
    handleSeeALLCLOButtonClick();
  };

  const CLImgName = 'certloc';

  return (
    <div className="clo-section">
      <div className="engage-certified-locator">
        <div className="static-content">
          <div className="title">Engage a Certified Locator</div>
          <div className="content">
            <img
              src={`../${CLImgName}.png`}
              srcSet={`../${CLImgName}.png, ../${CLImgName}@2x.png 2x, ../${CLImgName}@3x.png 3x`}
              alt="certloc certifications"
              className="certified-locator-icon"
            />
            <div className="text">
              Underground infrastructure networks are complex and require expert
              knowledge to identify the location of buried services carefully
              and safely. To reduce the risk of accidental damage or workplace
              injury, we highly recommend the services of a Certified Locator.
              The Certified Locator program is owned and operated by
              <a
                href="https://certloc.com.au/locators"
                target="_blank"
                rel="noopener noreferrer"
              >
                CERTLOC Global
              </a>
              .
            </div>
          </div>
        </div>

        {showCLO ? (
          <div className="certified-locators-listing">
            <div>
              <div className="header">
                <div className="title">
                  Certified Locating Organisations servicing this job site (in
                  random order)
                </div>
                <div className="controllers">
                  <div className="overflow-menu">
                    <OverflowMenu
                      light
                      flipped
                      ariaLabel={'Options'}
                      iconDescription={'Options'}
                    >
                      <OverflowMenuItem
                        itemText="Hide"
                        onClick={() => toggleCLOListing(false)}
                      />
                      <OverflowMenuItem
                        itemText="Don't show again"
                        onClick={() => toggleCLOListing(false, true)}
                      />
                    </OverflowMenu>
                  </div>
                  <div className="close">
                    <Close20 onClick={() => toggleCLOListing(false)} />
                  </div>
                </div>
              </div>
              {!loadingTopThreeCLO &&
                topThreeCLO?.LocatingOrganisations?.length > 0 && (
                  <>
                    <div className="listing-container">
                      {topThreeCLO?.LocatingOrganisations?.map((clo: any) => {
                        return (
                          <ClickableTile
                            key={clo.id}
                            className="locator-tile"
                            handleClick={() => handleTileClick(clo.id)}
                          >
                            <div className="locator-tile-name">{clo.name}</div>
                            <div className="locator-tile-desc">
                              {`${clo.details.locatorAmount} Certified Locator${
                                clo.details.locatorAmount > 1 ? 's' : ''
                              }`}
                            </div>
                            <div className="locator-tile-footer">
                              <ChevronRight16 />
                            </div>
                          </ClickableTile>
                        );
                      })}
                    </div>

                    <div className="all-locators-button">
                      {screenSize !== SCREEN_SIZE.SM && (
                        <Button
                          kind="tertiary"
                          renderIcon={ChevronRight16}
                          onClick={handleSeeAllCLOClick}
                          className="btn-custom"
                        >
                          See all Certified Locating Organisations servicing
                          this area
                        </Button>
                      )}

                      {screenSize === SCREEN_SIZE.SM && (
                        <div
                          className="btn-all-locators-sm"
                          onClick={handleSeeAllCLOClick}
                        >
                          <div className="btn-text">
                            See all Certified Locating Organisations servicing
                            this area
                          </div>
                          <Button hasIconOnly renderIcon={ChevronRight16} />
                        </div>
                      )}
                    </div>
                    <div className="text">
                      <span>About this list.</span>
                      When you lodge an enquiry, up to three Certified Locating
                      Organisations (CLOs) servicing your dig site are shown
                      here. These CLOs are selected at random to ensure that all
                      CLOs get the same opportunity to appear. You can also
                      access the full list with the &quot;See all ...&quot;
                      button.
                    </div>
                  </>
                )}
              {loadingTopThreeCLO && (
                <div className="listing-container">
                  {['', '', ''].map((ele: any, idx: number) => {
                    return (
                      <ClickableTile key={idx} className="locator-tile">
                        <div className="locator-tile-name">
                          {<InlineLoading description="Loading" />}
                        </div>
                        <div className="locator-tile-footer">
                          <ChevronRight16 />
                        </div>
                      </ClickableTile>
                    );
                  })}
                </div>
              )}
              {!loadingTopThreeCLO &&
                topThreeCLO?.LocatingOrganisations?.length <= 0 && (
                  <div className="no-result">
                    No Certified Locating Organisations to display
                  </div>
                )}
            </div>
          </div>
        ) : (
          ''
        )}

        <div className="clo-5p-link-container">
          {!showCLO && (
            <div
              className="clo-link-container"
              onClick={() => toggleCLOListing(true, true)}
            >
              <div className="link-image">
                {screenSize !== SCREEN_SIZE.SM ? <CLOLogo /> : <CLOLogo40 />}
              </div>
              <div className="link-text">Certified Locating Organisations</div>
            </div>
          )}

          <div
            className="five-p-link-container"
            onClick={() => handle5PsGuideClick()}
          >
            <div className="link-image">
              {screenSize !== SCREEN_SIZE.SM ? <FivePLogo /> : <FivePLogo40 />}
            </div>
            <div className="link-text">
              Minimise risk of damage or harm during excavation with use of the
              5Ps
            </div>
          </div>
        </div>
        <div className="why-use-certified-locator">
          <div className="title">Why use a Certified Locator?</div>
          <div className="paragraph">
            It&apos;s highly recommended to engage a Certified Locating
            Organisation/Certified Locator to help prevent accidental damage.
            The use of Certified Locators is required by some major utilities,
            such as Telstra.
          </div>
          <div className="paragraph">
            Certified Locators are nationally recognised for meeting the minimum
            industry standard for locating excellence, best practice and safety.
            They are skilled in interpreting utility plans, diligently
            investigating a site, and navigating complex utility networks, to
            locate and identify the underground utilities present at your
            worksite.
          </div>
          <div className="paragraph">
            The Certified Locator program is owned and operated by
            <a
              href="https://dbydlocator.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              CERTLOC Global
            </a>
            .
          </div>
        </div>
      </div>
    </div>
  );
};

export default CLOSection;
